import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent } from '../components/ui/card';
import { 
  ChevronDown, 
  ArrowLeft, 
  Calendar,
  BookOpen,
  Church,
  Heart,
  Moon,
  FileText,
  Download,
  ExternalLink,
  Globe
} from 'lucide-react';
import { AutoTranslate } from '../hooks/useAutoTranslate';

const MassesPage = () => {
  const { t, isRTL } = useLanguage();
  const [openSections, setOpenSections] = useState({});
  const location = useLocation();
  const massordRef = useRef(null);

  useEffect(() => {
    if (location.hash === '#massord') {
      setOpenSections((prev) => ({ ...prev, massorder: true }));
      const timer = setTimeout(() => {
        massordRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  const toggleSection = (id) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const sections = [
    {
      id: 'penance',
      icon: Moon,
      title: 'Kyrkans botdagar',
      color: 'from-violet-500 to-purple-600',
      content: {
        intro: 'Botdagar och bottider för hela Kyrkan är alla fredagar under hela året samt fastetiden. På dessa dagar skall de troende på ett särskilt sätt ägna sig åt bön och åt fromhets- och kärleksgärningar och åt självförsakelse genom att mer troget fullgöra sina skyldigheter.',
        items: [
          { 
            name: 'Askonsdag och Långfredag', 
            desc: 'De troende är förpliktade till fasta och abstinens. Fastebudet gäller alla vuxna tills de fyllt 60 år, abstinensen alla som fyllt 14 år.'
          },
          { 
            name: 'Fredagar', 
            desc: 'Alla som fyllt 14 år skall uppfylla den av kyrkan föreskrivna botgöringen.'
          },
        ],
        penanceExamples: [
          'Avstå från kött eller något annat slag av mat',
          'Avstå från alkohol eller rökning eller någon annan njutning',
          'Avstå från all mat längre än vanligt och skänka sparade pengar till nödlidande',
          'Ägna särskild uppmärksamhet åt familjens gemensamma bön',
          'Delta i den heliga mässan, sakramentstillbedjan eller korsvägsandakten',
          'Uppmärksamma de fattiga, sjuka, åldringar eller ensamma'
        ],
        link: 'https://www.katolskakyrkan.se/forsamlingsliv/ordo-och-veckans-lasningar/kyrkans-botdagar'
      }
    },
    {
      id: 'prayers',
      icon: Heart,
      title: 'Böner',
      color: 'from-rose-500 to-pink-600',
      content: {
        intro: 'Bönen är hjärtat i det kristna livet. Genom bönen samtalar vi med Gud, tackar för hans gåvor och ber om hans hjälp och vägledning. Här kan du ladda ner de viktigaste bönerna.',
        downloads: [
          { name: 'Herrens bön (Fader vår)', url: 'https://www.katolskakyrkan.se/media/3839/herrens-bon.pdf', icon: '🙏' },
          { name: 'Ave Maria', url: 'https://www.katolskakyrkan.se/media/3837/ave-maria.pdf', icon: '👼' },
          { name: 'Bön till skyddsängeln', url: 'https://www.katolskakyrkan.se/media/3841/till-skyddsangeln.pdf', icon: '😇' },
          { name: 'Bordsbön', url: 'https://www.katolskakyrkan.se/media/3838/bordsbon.pdf', icon: '🍞' },
          { name: 'Morgonbön', url: 'https://www.katolskakyrkan.se/media/3840/morgonbon.pdf', icon: '🌅' },
          { name: 'Aftonbön', url: 'https://www.katolskakyrkan.se/media/3836/aftonbon.pdf', icon: '🌙' },
        ],
        items: [
          { 
            name: 'Rosenkransen', 
            desc: 'Meditation över Kristi och Marias liv genom att be Ave Maria.'
          },
          { 
            name: 'Tidegärden', 
            desc: 'Kyrkans officiella dagliga bön som bes vid bestämda tider.'
          },
          { 
            name: 'Ängeln Herrens (Angelus)', 
            desc: 'Bön till minne av Inkarnationen, traditionellt kl. 6, 12 och 18.'
          },
        ],
        link: 'https://www.katolskakyrkan.se/forsamlingsliv/boner'
      }
    },
    {
      id: 'sundaysheet',
      icon: FileText,
      title: 'Söndagsblad för stora och små',
      color: 'from-amber-500 to-orange-600',
      content: {
        intro: 'För varje söndag finns "Söndagsblad för stora och små" att ladda ner och ta med till mässan. Söndagsbladen innehåller dagens evangelium, några frågor om evangeliet att fundera över samt en kort nyhetsnotis. På baksidan finns en fin bild för färgläggning.',
        items: [
          { 
            name: 'Veckans evangelium', 
            desc: 'Läs och reflektera över söndagens evangelietext.'
          },
          { 
            name: 'Diskussionsfrågor', 
            desc: 'Frågor för samtal i familjen eller gruppen.'
          },
          { 
            name: 'Aktiviteter för barn', 
            desc: 'Bild för färgläggning och anpassade berättelser.'
          },
        ],
        externalLink: {
          name: 'Ladda ner söndagsblad från KPN',
          url: 'https://kpn.se/resurser/sondagens-evangelium-for-barn'
        },
        link: 'https://www.katolskakyrkan.se/forsamlingsliv/sondagsblad-for-stora-och-sma'
      }
    },
    {
      id: 'massorder',
      icon: Globe,
      title: 'Mässordning på olika språk',
      color: 'from-blue-500 to-indigo-600',
      content: {
        intro: 'Ladda ner och skriv ut mässordningen på olika språk. Ta med till mässan så att du kan följa med på ditt eget språk.',
        downloads: [
          { name: 'Svenska', url: 'https://www.katolskakyrkan.se/media/1059/svenska_hafte.pdf', flag: '🇸🇪' },
          { name: 'Svenska/Engelska', url: 'https://www.katolskakyrkan.se/media/1405/massordning-engelska.pdf', flag: '🇬🇧' },
          { name: 'Svenska/Franska', url: 'https://www.katolskakyrkan.se/media/1058/franska-svenska_hafte.pdf', flag: '🇫🇷' },
          { name: 'Svenska/Tyska', url: 'https://www.katolskakyrkan.se/media/1060/tyska_svenska_hafte.pdf', flag: '🇩🇪' },
          { name: 'Svenska/Polska', url: 'https://www.katolskakyrkan.se/media/1061/polska-svenska_hafte.pdf', flag: '🇵🇱' },
          { name: 'Svenska/Spanska', url: 'https://www.katolskakyrkan.se/media/1062/spanska_svenska_hafte.pdf', flag: '🇪🇸' },
          { name: 'Svenska/Portugisiska', url: 'https://www.katolskakyrkan.se/media/1063/portugiska_svenska_hafte.pdf', flag: '🇵🇹' },
          { name: 'Svenska/Arabiska', url: 'https://www.katolskakyrkan.se/media/1057/arabiska_svenska_hafte.pdf', flag: '🇸🇦' },
          { name: 'Kroatiska', url: 'https://www.katolskakyrkan.se/media/3421/massa-pa-kroatiska.pdf', flag: '🇭🇷' },
        ],
        link: 'https://www.katolskakyrkan.se/forsamlingsliv/massordning-pa-olika-sprak'
      }
    },
  ];

  return (
    <div className="min-h-screen themed-page">
      <Navbar />

      {/* Header */}
      <section className="themed-hero relative h-80 overflow-hidden bg-gradient-to-r from-rose-700 via-pink-700 to-rose-800">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='80' height='80' viewBox='0 0 80 80' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M50 50c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10s-10-4.477-10-10 4.477-10 10-10zM10 10c0-5.523 4.477-10 10-10s10 4.477 10 10-4.477 10-10 10c0 5.523-4.477 10-10 10S0 25.523 0 20s4.477-10 10-10zm10 8c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm40 40c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z' /%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <Link to="/fordjupning" className={`inline-flex items-center text-rose-200 hover:text-white mb-4 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}>
              <ArrowLeft className={`w-4 h-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
              <AutoTranslate>Tillbaka till Fördjupning</AutoTranslate>
            </Link>
            <h1 className="responsive-hero-title font-serif font-bold text-white mb-4 fade-in">
              <AutoTranslate>Mässor och Böner</AutoTranslate>
            </h1>
            <p className="text-xl text-rose-100 fade-in">
              <AutoTranslate>Botdagar, böner, söndagsblad och mässordning</AutoTranslate>
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-4">
            {sections.map((section) => {
              const Icon = section.icon;
              const isOpen = openSections[section.id];
              
              return (
                <Card 
                  key={section.id} 
                  ref={section.id === 'massorder' ? massordRef : undefined}
                  id={section.id === 'massorder' ? 'massord' : undefined}
                  className="border-0 shadow-xl card-hover overflow-hidden transition-all duration-300 hover:shadow-xl scroll-mt-24"
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full text-left"
                    data-testid={`accordion-${section.id}`}
                  >
                    <div className={`flex items-center justify-between p-6 bg-gradient-to-r ${section.color} text-white ${isRTL ? 'flex-row-reverse' : ''}`}>
                      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                          <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="text-xl font-serif font-bold">
                          <AutoTranslate>{section.title}</AutoTranslate>
                        </h3>
                      </div>
                      <ChevronDown 
                        className={`w-6 h-6 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
                      />
                    </div>
                  </button>
                  
                  <div 
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                    }`}
                  >
                    <CardContent className="p-6 bg-white">
                      <p className="text-gray-700 mb-6 leading-relaxed">
                        <AutoTranslate>{section.content.intro}</AutoTranslate>
                      </p>
                      
                      {/* Regular items */}
                      {section.content.items && (
                        <div className="space-y-3 mb-6">
                          {section.content.items.map((item, idx) => (
                            <div 
                              key={`item-${item.name || idx}`}
                              className={`flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                            >
                              <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${section.color} mt-2 flex-shrink-0`} />
                              <div>
                                <p className="font-semibold text-gray-900">
                                  <AutoTranslate>{item.name}</AutoTranslate>
                                </p>
                                <p className="text-sm text-gray-600">
                                  <AutoTranslate>{item.desc}</AutoTranslate>
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Penance examples */}
                      {section.content.penanceExamples && (
                        <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <h4 className="font-semibold text-purple-800 mb-3">
                            <AutoTranslate>Exempel på botgöring:</AutoTranslate>
                          </h4>
                          <ul className="space-y-2">
                            {section.content.penanceExamples.map((example, idx) => (
                              <li key={`penance-${idx}-${String(example).substring(0, 20)}`} className={`flex items-start gap-2 text-sm text-purple-700 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <span className="text-purple-500 mt-1">•</span>
                                <AutoTranslate>{example}</AutoTranslate>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Prayer downloads */}
                      {section.content.downloads && section.id === 'prayers' && (
                        <div className="mb-6 p-4 bg-rose-50 rounded-lg border border-rose-200">
                          <h4 className={`font-semibold text-rose-800 mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Download className="w-4 h-4" />
                            <AutoTranslate>Ladda ner böner (PDF)</AutoTranslate>
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {section.content.downloads.map((doc, idx) => (
                              <a 
                                key={`prayer-dl-${doc.name || doc.url || idx}`}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 text-rose-700 hover:text-rose-900 hover:bg-rose-100 p-2 rounded transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                              >
                                <span className="text-lg">{doc.icon}</span>
                                <span className="text-sm"><AutoTranslate>{doc.name}</AutoTranslate></span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Mass order downloads in different languages */}
                      {section.content.downloads && section.id === 'massorder' && (
                        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <h4 className={`font-semibold text-blue-800 mb-3 flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <Download className="w-4 h-4" />
                            <AutoTranslate>Ladda ner mässordning (PDF)</AutoTranslate>
                          </h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {section.content.downloads.map((doc, idx) => (
                              <a 
                                key={`mass-dl-${doc.name || doc.url || idx}`}
                                href={doc.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-center gap-2 text-blue-700 hover:text-blue-900 hover:bg-blue-100 p-2 rounded transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                              >
                                <span className="text-lg">{doc.flag}</span>
                                <span className="text-sm">{doc.name}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* External link for Sunday sheets */}
                      {section.content.externalLink && (
                        <div className="mb-6">
                          <a 
                            href={section.content.externalLink.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`inline-flex items-center px-4 py-3 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg transition-colors font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
                          >
                            <Download className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                            <AutoTranslate>{section.content.externalLink.name}</AutoTranslate>
                          </a>
                        </div>
                      )}
                      
                      <a 
                        href={section.content.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`inline-flex items-center text-sm text-rose-600 hover:text-rose-800 font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
                      >
                        <ExternalLink className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                        <AutoTranslate>Läs mer på katolskakyrkan.se</AutoTranslate>
                      </a>
                    </CardContent>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Mass Schedule CTA */}
          <Card className="mt-12 border-0 shadow-xl bg-gradient-to-r from-rose-50 to-pink-50">
            <CardContent className="p-8 text-center">
              <Calendar className="w-12 h-12 text-rose-600 mx-auto mb-4" />
              <h3 className="text-2xl font-serif font-bold text-gray-900 mb-3">
                <AutoTranslate>Se mässtider i våra kyrkor</AutoTranslate>
              </h3>
              {/*<p className="text-gray-600 mb-6">
                <AutoTranslate>Hitta mässtider i våra kyrkor</AutoTranslate>
              </p>*/}
              <Link 
                to="/liturgi"
                className={`inline-flex items-center px-6 py-3 bg-rose-500 hover:bg-rose-600 text-white font-semibold rounded-lg transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                <Calendar className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                <AutoTranslate>Se liturgi</AutoTranslate>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MassesPage;
